// authUtils.ts
import Cookies from 'js-cookie'
import api from '../utils/api'
import { setAppState, resetApp } from '../redux/app/reducers'
import { setUser, resetUser } from '../redux/user/reducers'
import { AppState, LayoutType, Store, SelectedStore } from '../redux/app/types'
import store from '../redux/store'
import { extractPlanLimits, extractAvailableModules, extractPricingPlan } from './jwt'

const ACCESS_TOKEN_KEY = 'sh_sad_access_token'
const REFRESH_TOKEN_KEY = 'sh_sad_refresh_token'

export const setAuthTokens = (accessToken: string, refreshToken: string) => {
  Cookies.set(ACCESS_TOKEN_KEY, accessToken, { secure: true, sameSite: 'strict' })
  Cookies.set(REFRESH_TOKEN_KEY, refreshToken, { secure: true, sameSite: 'strict' })
}

export const getAccessToken = () => Cookies.get(ACCESS_TOKEN_KEY)
export const getRefreshToken = () => Cookies.get(REFRESH_TOKEN_KEY)

export const clearAuthTokens = () => {
  Cookies.remove(ACCESS_TOKEN_KEY)
  Cookies.remove(REFRESH_TOKEN_KEY)
}

export const refreshAuthTokens = async () => {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  try {
    const response = await api.get(`/auth/refresh-token?token=${refreshToken}`)
    const { access_token, refresh_token, user, selected_store, stores } = response

    setAuthTokens(access_token, refresh_token)

    const appState = createAppStateWithTokenData(access_token, refresh_token, selected_store, stores, false)

    store.dispatch(setUser(user))
    store.dispatch(setAppState(appState))

    return true
  } catch (error) {
    console.error('Failed to refresh token:', error)
    clearAuthTokens()
    return false
  }
}

export const logoutUser = async () => {
  clearAuthTokens()
  store.dispatch(resetUser())
  store.dispatch(resetApp())
}

export const createAppStateWithTokenData = (
  accessToken: string,
  refreshToken: string,
  selectedStore: SelectedStore,
  stores: Store[],
  isMobile = false,
): AppState => {
  // Parse JWT token to extract plan limits and available modules
  const planLimits = extractPlanLimits(accessToken)
  const availableModules = extractAvailableModules(accessToken)
  const pricingPlan = extractPricingPlan(accessToken)

  const appState: AppState = {
    isAuth: true,
    isAppLoading: false,
    token: {
      accessToken,
      refreshToken,
    },
    selectedStore: selectedStore || ({} as SelectedStore),
    stores: stores || [],
    layout: {
      type: LayoutType.SIDEBAR,
      isMobile,
    },
    planLimits: planLimits || undefined,
    availableModules,
    pricingPlan: pricingPlan || undefined,
  }

  return appState
}
